import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Eye, Trash2, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API_URL}/inquiries`);
      setInquiries(response.data);
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/inquiries/${deleteTarget}`);
      toast.success('刪除成功');
      fetchInquiries();
    } catch (error) {
      toast.error('刪除失敗');
    } finally {
      setDeleteTarget(null);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/inquiries/${id}`, { status });
      toast.success('狀態已更新');
      fetchInquiries();
      setDialogOpen(false);
    } catch (error) {
      toast.error('更新失敗');
    }
  };

  const viewInquiry = (inquiry) => {
    setSelectedInquiry(inquiry);
    setDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '新查詢':
        return 'bg-blue-100 text-blue-800';
      case '處理中':
        return 'bg-yellow-100 text-yellow-800';
      case '已完成':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">查詢管理</h1>
          <p className="text-muted-foreground">管理來自聯絡表單的查詢</p>
        </div>

        {loading ? (
          <div className="text-center py-12">載入中...</div>
        ) : inquiries.length > 0 ? (
          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="text-left p-4">姓名</th>
                  <th className="text-left p-4">電話</th>
                  <th className="text-left p-4">課程</th>
                  <th className="text-left p-4">狀態</th>
                  <th className="text-left p-4">提交時間</th>
                  <th className="text-right p-4">操作</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((inquiry) => (
                  <tr key={inquiry.id} className="border-b last:border-0">
                    <td className="p-4 font-medium">{inquiry.name}</td>
                    <td className="p-4">{inquiry.phone}</td>
                    <td className="p-4">{inquiry.course || '-'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                    </td>
                    <td className="p-4">{new Date(inquiry.created_at).toLocaleString('zh-HK')}</td>
                    <td className="p-4 text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => viewInquiry(inquiry)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(inquiry.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border">
            <p className="text-muted-foreground">還沒有收到任何查詢</p>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>查詢詳情</DialogTitle>
            </DialogHeader>
            {selectedInquiry && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">姓名</label>
                    <p className="text-lg">{selectedInquiry.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">狀態</label>
                    <div className="mt-1">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedInquiry.status)}`}>
                        {selectedInquiry.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      電話
                    </label>
                    <a href={`tel:${selectedInquiry.phone}`} className="text-primary hover:underline">
                      {selectedInquiry.phone}
                    </a>
                  </div>
                  {selectedInquiry.email && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        電郵
                      </label>
                      <a href={`mailto:${selectedInquiry.email}`} className="text-primary hover:underline">
                        {selectedInquiry.email}
                      </a>
                    </div>
                  )}
                </div>

                {selectedInquiry.course && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">感興趣的課程</label>
                    <p>{selectedInquiry.course}</p>
                  </div>
                )}

                {selectedInquiry.message && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">留言</label>
                    <p className="mt-1 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">
                      {selectedInquiry.message}
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-muted-foreground">提交時間</label>
                  <p>{new Date(selectedInquiry.created_at).toLocaleString('zh-HK')}</p>
                </div>

                <div className="pt-4 border-t">
                  <label className="text-sm font-medium text-muted-foreground mb-2 block">更新狀態</label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={selectedInquiry.status === '新查詢' ? 'default' : 'outline'}
                      onClick={() => updateStatus(selectedInquiry.id, '新查詢')}
                    >
                      新查詢
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedInquiry.status === '處理中' ? 'default' : 'outline'}
                      onClick={() => updateStatus(selectedInquiry.id, '處理中')}
                    >
                      處理中
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedInquiry.status === '已完成' ? 'default' : 'outline'}
                      onClick={() => updateStatus(selectedInquiry.id, '已完成')}
                    >
                      已完成
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>確定要刪除這筆查詢？</AlertDialogTitle>
              <AlertDialogDescription>此操作不可撤銷，查詢記錄將永久刪除。</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>取消</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminInquiries;
